import type { JsonObject } from "@bufbuild/protobuf";
import { getVersion } from "@stepwisehq/prosemirror-collab-commit/collab-commit";
import { isEmpty } from "es-toolkit/compat";
import { type Getter, type Setter, atom } from "jotai";
import type { Schema } from "prosemirror-model";
import type { EditorState } from "prosemirror-state";

import { serializePage } from "@moment/api-collab/index";
import { Logger } from "@moment/logging";

import { type DocsListLocalEntry } from "../document-list/document-list.types";
import {
	agentInstructionPersistFilePaths,
	isAgentInstructionFilename,
	isAgentInstructionPage,
} from "../document/agents-md.atom";
import { fs } from "../filesystem/filesystem.atom";
import { getPageFilePath } from "../paths/paths.internal";
import { $mdPageCollabConnection } from "../reboot/collab-connection.atom";
import type { PageRefProps } from "../reboot/refs";
import {
	makeMdPageReceivePresenceCommand,
	makeMdPageRemoteChangesCommand,
	makeMdPageSendToCollabCommand,
	makeMdPageUpdatePresenceCommand,
} from "../reboot/utils";
import { prosemirrorClientId } from "./client-id";
import { $loadEditorState, $setEditorStateError } from "./editor-state.atom";
import { PmRebootLoadError } from "./errors";
import type { PluginFactory } from "./plugins-factory.atom";
import { resolveCurrentPagePath } from "./util";

const logger = new Logger("load-editor-state-from-reboot");
const fsApi = fs();

export interface LoadEditorStateFromRebootInput extends PageRefProps {
	key: string;
	pluginFactory: PluginFactory;
	schema: Schema;
	readonly: boolean;
	userId: string;
	onPersist?: (get: Getter, set: Setter, state: EditorState) => Promise<void>;
	localEntry?: DocsListLocalEntry;
	/** Optional explicit path from pageMetadata (e.g., "daily/2026-01-22.md") */
	pagePath?: string;
}

/**
 * Loads an editor state from the Reboot collab server.
 */
export const $loadEditorStateFromReboot = atom(
	null,
	async (get, set, input: LoadEditorStateFromRebootInput) => {
		const {
			key,
			pluginFactory,
			schema,
			readonly,
			userId,
			onPersist,
			localEntry,
			pagePath,
			...props
		} = input;
		const { documentId, pageId } = props;

		try {
			// Get the collab connection
			const connection = await get($mdPageCollabConnection(props));
			if (!connection) {
				throw new Error("Reboot connection not available");
			}

			const { authority, presence, context } = connection;

			// Fetch the initial document from Reboot
			const pageResponse = await authority.page(context);
			if (!pageResponse.doc) {
				throw new Error("Document is undefined");
			}

			const doc = pageResponse.doc.toJson() as JsonObject;
			const version = pageResponse.version ?? 0;

			// Create plugins using the factory with the version from Reboot
			const plugins = pluginFactory({ documentId, pageId, schema, readonly, version });

			// Create collab callbacks
			const sendToCollab = makeMdPageSendToCollabCommand(authority, context);
			const remoteChanges = makeMdPageRemoteChangesCommand(authority, context, version);
			const receivePresence = makeMdPageReceivePresenceCommand(presence, context);
			const updatePresence = makeMdPageUpdatePresenceCommand(presence, context, userId);

			let onPersist:
				| ((get: Getter, set: Setter, state: EditorState) => Promise<void>)
				| undefined = undefined;
			if (!isEmpty(localEntry) && localEntry.path) {
				// Use the explicit pagePath from metadata if available, otherwise fall back to default
				const pageFilePath = await getPageFilePath(localEntry.path, pageId, pagePath);
				// Resolve the parent directory now so we can ensure it exists before writing.
				// This is needed when a collaborator adds a page while this client is offline —
				// the pages/{pageId}/ directory won't exist on disk yet.
				const parentDir = await get(fsApi.dirname(pageFilePath));

				const resolvedFilename = pageFilePath.split("/").pop()?.toLowerCase();
				const isAgentFile = pagePath
					? isAgentInstructionPage(pagePath)
					: isAgentInstructionFilename(resolvedFilename);
				const persistFilePaths = isAgentFile
					? agentInstructionPersistFilePaths(localEntry.path, pageFilePath)
					: [pageFilePath];

				// Immediately persist the initial Reboot content to disk. When the owner
				// reconnects to collab after being offline, pages may have been added or
				// modified by collaborators and won't exist on disk yet. Write them now so
				// the local files catch up without requiring the user to edit each page.
				// The managed section (if any) is inside a readonly_section ProseMirror
				// node and round-trips through serialization — no runtime merge needed.
				await set(fsApi.mkdir, { dirPath: parentDir, options: { recursive: true } });
				const initialContent = serializePage(schema.nodeFromJSON(doc));
				await Promise.all(
					persistFilePaths.map((filePath) =>
						set(fsApi.writeTextFile, {
							filePath,
							content: initialContent,
							documentId,
						})
					)
				);
				logger.info("Persisted initial Reboot content to disk", {
					documentId,
					pageId,
					filePaths: persistFilePaths,
				});

				let lastPersistedVersion = version;
				onPersist = async (injectedGet, injectedSet, state: EditorState): Promise<void> => {
					////////////////////////////////////////////////////////////////////////////////
					// IMPORTANT: VERY SENSITIVE ZONE. READ BEFORE CHANGING.
					//
					// USE THE `injectedGet` AND `injectedSet` HERE. DO NOT USE `get` AND `set`.
					// `onPersist` is called from atom effects, which do work to make sure writes to
					// atoms we are reading from do not cause infinite loops. You MUST use those
					// injected getters and setters, or in collab mode, this function will persist
					// in a loop forever.
					const currentVersion = getVersion(state) ?? 0;
					if (currentVersion <= lastPersistedVersion) {
						return;
					}
					const currentPagePath = await resolveCurrentPagePath(
						injectedGet,
						documentId,
						pageId,
						pagePath
					);
					const currentPageFilePath = await getPageFilePath(
						localEntry.path,
						pageId,
						currentPagePath
					);
					const currentFilename = currentPageFilePath.split("/").pop()?.toLowerCase();
					const currentIsAgentFile = currentPagePath
						? isAgentInstructionPage(currentPagePath)
						: isAgentInstructionFilename(currentFilename);
					const currentPersistFilePaths = currentIsAgentFile
						? agentInstructionPersistFilePaths(localEntry.path, currentPageFilePath)
						: [currentPageFilePath];
					const currentParentDir = await injectedGet(fsApi.dirname(currentPageFilePath));
					const content = serializePage(state.doc);
					await injectedSet(fsApi.mkdir, {
						dirPath: currentParentDir,
						options: { recursive: true },
					});
					await Promise.all(
						currentPersistFilePaths.map((filePath) =>
							injectedSet(fsApi.writeTextFile, { filePath, content, documentId })
						)
					);

					lastPersistedVersion = currentVersion;
					// IMPORTANT: END SYNCHRONOUS ZONE. ////////////////////////////////////////////
					////////////////////////////////////////////////////////////////////////////////
				};
			} else if (input.onPersist) {
				onPersist = input.onPersist;
			}

			// Load the editor state. NEVER SSR, obviously, as this is from Reboot.
			set($loadEditorState, {
				key,
				doc,
				plugins,
				ssr: false,
				sendToCollab,
				remoteChanges,
				onPersist,
				receivePresence,
				updatePresence,
				localClientId: prosemirrorClientId,
				connection,
			});
		} catch (err) {
			const error = new PmRebootLoadError(err);
			logger.error("Failed to load editor state from Reboot", { key, err });
			set($setEditorStateError, { key, error });
		}
	}
);
$loadEditorStateFromReboot.debugLabel = "loadEditorStateFromReboot";
